import { Controller } from '@hotwired/stimulus'
import { Popover } from 'bootstrap'

export default class extends Controller {
  static targets = ['button', 'contentContainer', 'liveContainer', 'filterContainer']
  static values = {
    bodyClasses: Array,
    announcement: String
  }

  connect() {
    if (!this.hasButtonTarget) {
      throw new Error('Popover requires button target')
    }
    if (!this.hasContentContainerTarget) {
      throw new Error('Popover requires content container target')
    }

    // TODO: Figure out a solution here that does not rely on filter-container
    let container
    if (this.hasFilterContainerTarget) {
      container = this.filterContainerTarget
    } else {
      let containerParent
      try {
        containerParent = this.element.closest('*:has(#filter-container)')
      } catch {
        // browser does not support :has selector (e.g. firefox <= 120)
        // in current configuration should always be the parent form
        containerParent = this.element.closest('form')
      }
      if (containerParent == null) {
        // in some rare cases (e.g. volunteer opportunities page) there is no form parent,
        // so we will just take the first #filter-container in the document to avoid an error
        containerParent = document
        console.error('Could not locate filter container in DOM')
      }
      container = containerParent.querySelector('#filter-container')
    }
    this.popover = Popover.getOrCreateInstance(this.buttonTarget, {
      html: true,
      sanitize: false,
      container,
      placement: 'bottom',
      content: this.contentContainerTarget.innerHTML,
      popperConfig(defaultBsPopperConfig) {
        return { ...defaultBsPopperConfig, strategy: 'fixed' }
      }
    })

    // Bootstrap caches the popover content above and clones it into the visible
    // popover on show. The .d-none source remains inside the <form>, so without
    // intervention both copies would submit their inputs. While the popover is
    // showing we strip names from the source so only the clone contributes; while
    // hidden the source keeps its names so it carries forward the current filter
    // value for any submission triggered by another filter's Apply button.
    document.addEventListener('click', this.clickListener, true)
    document.addEventListener('turbo:morph', this.restoreOpenStateAfterMorph)

    this.buttonTarget.addEventListener('keydown', e => {
      const isExpanded = this.buttonTarget.getAttribute('aria-expanded')
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        this.popover.toggle()
      } else if (e.key === 'Tab' && isExpanded === 'true') {
        e.preventDefault()
        this.getPopoverContent().focus()
      }
    })

    this.buttonTarget.addEventListener('hidden.bs.popover', () => {
      this.buttonTarget.setAttribute('aria-expanded', false)
      if (this.hasLiveContainerTarget && this.hasAnnouncementValue) {
        this.liveContainerTarget.innerHTML = ''
      }
      this.contentContainerTarget.querySelectorAll('[data-suspended-name]').forEach(el => {
        el.setAttribute('name', el.dataset.suspendedName)
        delete el.dataset.suspendedName
      })
    })

    this.buttonTarget.addEventListener('show.bs.popover', () => {
      // close all other popovers
      document.querySelectorAll('[data-popover-target="button"]').forEach(button => {
        if (button !== this.buttonTarget) {
          Popover.getInstance(button).hide()
        }
      })
      this.suspendSourceNames()
    })

    // A page refresh morphs the page; keeping the open popover permanent stops the morph
    // from removing it along with whatever was typed into it
    this.buttonTarget.addEventListener('inserted.bs.popover', () => {
      this.popover.tip.setAttribute('data-turbo-permanent', '')
    })

    this.buttonTarget.addEventListener('shown.bs.popover', () => {
      this.buttonTarget.setAttribute('aria-expanded', true)
      if (this.hasLiveContainerTarget && this.hasAnnouncementValue) {
        this.liveContainerTarget.innerHTML = this.announcementValue
      }
      if (this.hasBodyClassesValue) {
        this.getPopoverBody().classList.add(...this.bodyClassesValue)
      }
      this.getPopoverContent().addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          this.popover.hide()
          this.buttonTarget.focus()
        }
      })

      // Mount React components in the popover content
      document.dispatchEvent(new Event('react:mount'))
    })
  }

  disconnect() {
    document.removeEventListener('click', this.clickListener, true)
    document.removeEventListener('turbo:morph', this.restoreOpenStateAfterMorph)
  }

  suspendSourceNames() {
    this.contentContainerTarget.querySelectorAll('[name]').forEach(el => {
      el.dataset.suspendedName = el.getAttribute('name')
      el.removeAttribute('name')
    })
  }

  // The morph resets the button and the hidden source inputs to the server's markup
  restoreOpenStateAfterMorph = () => {
    const tip = this.popover?.tip
    if (!tip?.isConnected) return

    this.buttonTarget.setAttribute('aria-expanded', true)
    this.buttonTarget.setAttribute('aria-describedby', tip.id)
    this.suspendSourceNames()
  }

  getPopoverBody() {
    return document.querySelector('.popover-body')
  }

  getPopoverContent() {
    return this.getPopoverBody().querySelector('#popover-content')
  }

  clickListener = e => {
    const { target } = e
    if (!this.popover) return
    // do not intercept normal click behavior for button target
    if (this.buttonTarget === target || this.buttonTarget.contains(target)) return

    const popoverBodyElement = this.getPopoverBody()
    if (popoverBodyElement && !popoverBodyElement.contains(target)) {
      this.popover.hide()
    }
  }
}
